import React, { useEffect, useMemo, useState } from "react";
import {
  FiCheck,
  FiChevronLeft,
  FiChevronRight,
  FiImage,
  FiPlus,
  FiSearch,
} from "react-icons/fi";

import {
  searchMedia,
  toAbsolutePublicUrl,
} from "../../../../services/mediaLibraryServices";

import AdminButton from "./../../_shared/ui/AdminButton";
import AdminDrawer from "./../../_shared/ui/AdminDrawer";
import AdminField from "./../../_shared/ui/AdminField";

import { MEDIA_ALBUM_PICKER_DEFAULT_CATEGORY } from "../data/mediaAlbumsAdminData";

export default function MediaAlbumMediaPickerDrawer({
  open,
  onClose,
  onConfirm,
}) {
  const [q, setQ] = useState("");
  const [category, setCategory] = useState(MEDIA_ALBUM_PICKER_DEFAULT_CATEGORY);
  const [items, setItems] = useState([]);
  const [selectedMap, setSelectedMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 24;

  const selectedItems = useMemo(() => {
    return Object.values(selectedMap);
  }, [selectedMap]);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    const fetchMedia = async () => {
      try {
        setLoading(true);

        const res = await searchMedia({
          q: q.trim() || undefined,
          category: category.trim() || undefined,
          type: "image",
          is_public: true,
          page,
          limit,
          sort: "created_at",
          dir: "DESC",
        });

        if (cancelled) return;

        setItems(Array.isArray(res?.items) ? res.items : []);
        setTotalPages(Number(res?.totalPages || 1));
      } catch (error) {
        console.error("Erreur searchMedia picker album :", error);

        if (!cancelled) {
          setItems([]);
          setTotalPages(1);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchMedia();

    return () => {
      cancelled = true;
    };
  }, [open, q, category, page]);

  useEffect(() => {
    if (!open) {
      setSelectedMap({});
      setPage(1);
    }
  }, [open]);

  const toggleSelect = (media) => {
    if (!media?.id) return;

    setSelectedMap((previous) => {
      const next = { ...previous };

      if (next[media.id]) {
        delete next[media.id];
      } else {
        next[media.id] = media;
      }

      return next;
    });
  };

  const handleConfirm = async () => {
    if (selectedItems.length === 0) return;

    try {
      setConfirming(true);
      await onConfirm?.(selectedItems);
      setSelectedMap({});
    } finally {
      setConfirming(false);
    }
  };

  return (
    <AdminDrawer
      open={open}
      onClose={onClose}
      title="Ajouter des photos"
      subtitle="Sélectionnez une ou plusieurs images depuis la médiathèque."
      icon={FiImage}
      size="full"
      zIndex="z-[100]"
      footer={
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs font-bold text-slate-500 dark:text-slate-400">
            Page {page} / {totalPages} · {selectedItems.length} sélectionnée(s)
          </p>

          <div className="flex flex-wrap gap-2">
            <AdminButton
              icon={FiChevronLeft}
              variant="secondary"
              disabled={page <= 1 || loading}
              onClick={() => setPage((previous) => Math.max(previous - 1, 1))}
            >
              Précédent
            </AdminButton>

            <AdminButton
              rightIcon={FiChevronRight}
              variant="secondary"
              disabled={page >= totalPages || loading}
              onClick={() =>
                setPage((previous) => Math.min(previous + 1, totalPages))
              }
            >
              Suivant
            </AdminButton>

            <AdminButton
              icon={FiPlus}
              variant="primary"
              loading={confirming}
              disabled={selectedItems.length === 0}
              onClick={handleConfirm}
            >
              Ajouter {selectedItems.length || ""}
            </AdminButton>

            <AdminButton variant="dark" onClick={onClose}>
              Fermer
            </AdminButton>
          </div>
        </div>
      }
    >
      <div className="space-y-5">
        <div className="grid gap-4 rounded-[1.7rem] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900 md:grid-cols-[1fr_260px]">
          <AdminField label="Rechercher">
            <div className="flex min-h-12 items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 transition focus-within:border-sky-400 focus-within:ring-4 focus-within:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:focus-within:border-sky-500 dark:focus-within:ring-sky-500/10">
              <FiSearch className="text-slate-400" />

              <input
                value={q}
                onChange={(event) => {
                  setPage(1);
                  setQ(event.target.value);
                }}
                placeholder="Titre, tags, nom de fichier..."
                className="h-11 min-w-0 flex-1 bg-transparent text-sm font-medium text-slate-800 outline-none placeholder:text-slate-400 dark:text-slate-100"
              />
            </div>
          </AdminField>

          <AdminField
            label="Catégorie"
            help="Laissez vide pour afficher toutes les catégories."
          >
            <input
              value={category}
              onChange={(event) => {
                setPage(1);
                setCategory(event.target.value);
              }}
              placeholder="commune, accueil, patrimoine..."
              className="min-h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
            />
          </AdminField>
        </div>

        {selectedItems.length > 0 ? (
          <div className="rounded-[1.5rem] border border-sky-200 bg-sky-50 px-4 py-3 text-sm font-bold text-sky-800 dark:border-sky-500/20 dark:bg-sky-500/10 dark:text-sky-300">
            {selectedItems.length} photo(s) sélectionnée(s). Cliquez sur
            “Ajouter” pour les insérer dans l’album.
          </div>
        ) : null}

        {loading ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6">
            {Array.from({ length: 12 }).map((_, index) => (
              <div
                key={index}
                className="overflow-hidden rounded-[1.5rem] border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900"
              >
                <div className="aspect-[4/3] animate-pulse bg-slate-200 dark:bg-slate-800" />
                <div className="space-y-2 p-3">
                  <div className="h-3 w-3/4 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
                  <div className="h-3 w-1/2 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
                </div>
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="flex min-h-[260px] items-center justify-center rounded-[1.7rem] border border-dashed border-slate-300 bg-slate-50 p-8 text-center dark:border-slate-700 dark:bg-slate-900">
            <div>
              <FiImage className="mx-auto text-5xl text-slate-300 dark:text-slate-600" />

              <h3 className="mt-4 text-lg font-bold text-slate-950 dark:text-white">
                Aucune image trouvée
              </h3>

              <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                Vérifiez les filtres ou ajoutez des images dans la médiathèque.
              </p>
            </div>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6">
            {items.map((media) => {
              const url = toAbsolutePublicUrl(media.url);
              const selected = !!selectedMap[media.id];

              return (
                <button
                  key={media.id}
                  type="button"
                  onClick={() => toggleSelect(media)}
                  className={`group relative overflow-hidden rounded-[1.5rem] border bg-white text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-xl hover:shadow-slate-200/70 dark:bg-slate-900 dark:hover:shadow-black/20 ${
                    selected
                      ? "border-sky-400 ring-4 ring-sky-100 dark:border-sky-500 dark:ring-sky-500/10"
                      : "border-slate-200 hover:border-sky-300 dark:border-slate-800 dark:hover:border-sky-500/30"
                  }`}
                >
                  <div className="aspect-[4/3] overflow-hidden bg-slate-100 dark:bg-slate-800">
                    <img
                      src={url}
                      alt={media.alt_text || media.title || media.file_name}
                      className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                    />
                  </div>

                  <div className="p-3">
                    <p className="line-clamp-2 text-sm font-bold text-slate-900 dark:text-white">
                      {media.title || media.file_name || "Image sans titre"}
                    </p>

                    <p className="mt-1 line-clamp-1 text-xs font-medium text-slate-500 dark:text-slate-400">
                      {media.category || "Sans catégorie"}
                    </p>
                  </div>

                  {selected ? (
                    <div className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-sky-600 text-white shadow-lg">
                      <FiCheck />
                    </div>
                  ) : null}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </AdminDrawer>
  );
}